import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useForm, FieldValues, UseFormReturn } from 'react-hook-form';
import type { Questionnaire } from '@/typings/Questionnaire';
import { addressSchema } from '@/components/DesignLibrary/Atoms/AddressUS/AddressUS';
import getFieldValue from '@helpers/questionnaires/getFieldValue';
import evaluateExpression from '@helpers/evaluateExpression';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import _ from 'lodash';
import { QuestionnaireResponse, UpdateResponseData } from '@/typings/questionnaires-api-models';
import checkFieldValidity from '@helpers/questionnaires/checkFieldValidity';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { Creditor } from '@/typings/api-models';
import trackEvents from '@/helpers/trackEvents';
import useQuestionnaireInspector from './useQuestionnaireInspector';
import useLoadAiAgents, { AiAgent } from './useLoadAiAgents';

type FormContextType = {
  aiAgents: AiAgent[] | null;
  aiAgentsReadyToRun: boolean;
  checkFieldValidity: typeof checkFieldValidity;
  formMethods: UseFormReturn<FieldValues>;
  currentSection: string;
  defaultValues: Record<string, unknown>;
  responseSources: Record<string, QuestionnaireResponse['source']>;
  referenceData: QuestionnaireApiModels.DataReference[];
  setCurrentSection: (sectionId: string) => void;
  getSectionProgress: (sectionId: string, fieldsVisibility: Record<string, boolean>) => number;
  sectionVisibility: Record<string, boolean>;
  setSectionVisibility: (sectionId: string, isVisible: boolean) => void;
  getFieldSource: (fieldKey: string) => QuestionnaireResponse['source'];
  setFieldSource: (fieldKey: string, source: QuestionnaireResponse['source']) => void;
  getAiRequestId: (fieldKey: string) => string | null;
  setAiRequestId: (fieldKey: string, aiRequestId:string) => void;
  setReferenceData: (data: QuestionnaireApiModels.DataReference[]) => void;
  getSubsectionProgress: (
    sectionId: string,
    subsection: string,
    fieldsVisibility: Record<string, boolean>,
  ) => {
    completedFields: number;
    subsectionProgressPercentage: number | null;
    totalFields: number;
  } | null;
  sectionErrors: { [key: string]: boolean };
  setSectionErrors: (errors: { [key: string]: boolean }) => void;
  onSubmit: (responses: UpdateResponseData, isCompleted?: boolean) => Promise<boolean>;
  aiAutofillActions: string[];
  fieldsByKey: Record<string, Questionnaire.Field>;
  fieldsById: Record<string, Questionnaire.Field>;
  userWorkflowId: string | null;
  questionnaireId: string;
  setValue: (arg1: { key: string, value: unknown, source?: QuestionnaireResponse['source'], aiRequestId?:string }) => void;
  refetchCreditors: () => void;
  creditors: Creditor[];
};

const FormContext = createContext<FormContextType | undefined>(undefined);

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

type FormProviderProps = {
  aiAutofillActions: string[];
  fieldsByKey: Record<string, Questionnaire.Field>;
  formData: Questionnaire.Questionnaire;
  questionnaireResponses: Array<
    & Pick<QuestionnaireApiModels.QuestionnaireResponse, 'id' | 'value' | 'source' | 'associatedWithId' | 'aiRequestId'>
    & {
      listResponses: Array<
        & Pick<QuestionnaireApiModels.QuestionnaireResponse, 'value' | 'index' | 'source' | 'aiRequestId'>
        & {
          fieldTemplate: Pick<QuestionnaireApiModels.FieldTemplate, 'key'>;
        }
      > | null;
      fieldTemplate: Pick<QuestionnaireApiModels.FieldTemplate, 'type' | 'key'>;
    }
  >;
  children: React.ReactNode;
  onSubmit: (responses: UpdateResponseData, isCompleted?: boolean) => Promise<boolean>;
  userWorkflowId: string | null;
};

const validatePresence = (value: unknown, requiredFields: string[]): string | boolean => {
  if (typeof value === 'object' && value !== null) {
    const isValid = requiredFields.every(field => {
      const fieldValue = (value as Record<string, unknown>)[field];
      return field in value && fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
    });
    return isValid ? true : 'Required';
  }
  return 'Required';
};

const validateName = (value: unknown): string | boolean => validatePresence(value, ['firstName', 'lastName']);

const validateCurrency = (value: unknown): string | boolean => validatePresence(value, ['amount']);

const validateAddress = (value: unknown, fieldNames: string[]): string | boolean => validatePresence(value, fieldNames);

const validateUSAddress = (value: unknown, required: boolean): boolean | string => {
  const areAllFieldsEmpty = (address: unknown): boolean => {
    if (!address || address === null) {
      return true;
    }

    const { city, state, zip, line1 } = address as { [key: string]: string | undefined };

    return [city, state, zip, line1].every(field => !field || field.trim() === '');
  };

  if (!required && areAllFieldsEmpty(value)) {
    return true;
  }

  const { city, state, zip, line1 } = value as { [key: string]: string | undefined };

  const isAnyFieldFilled = !!(city || state || zip || line1);

  const shouldBeRequired = required || isAnyFieldFilled;

  const schema = addressSchema(shouldBeRequired);

  const result = schema.safeParse(value);

  if (result.success) {
    return true;
  }

  return result.error.errors[0].message || 'Invalid address';
};

const validateIntlAddress = (value: unknown): string | boolean => validateAddress(value, ['line1', 'city', 'postCode']);

export const FormProvider: React.FC<FormProviderProps> = ({
  aiAutofillActions,
  fieldsByKey,
  formData,
  questionnaireResponses,
  children,
  onSubmit,
  userWorkflowId,
}) => {
  trackEvents.track('Render FormProvider');
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const responseSources = useRef<Record<string, QuestionnaireResponse['source']>>({});
  const aiRequestIdRef = useRef<Record<string, string | null>>({});
  const fieldsByKeyRef = useRef(fieldsByKey);
  fieldsByKeyRef.current = fieldsByKey;
  const formMethods = useForm({
    values: formValues,
  });

  const { data: creatorCreditors, getData: getCreatorCreditorsFn } = useNoodleApi(tsClient.creators.getCreatorCreditors);

  useQuestionnaireInspector({
    fieldsByKey,
    formMethods,
    responseSourcesRef: responseSources,
    sections: formData.sections,
  });

  const aiAgents = useLoadAiAgents({
    creatorId: formData.questionnaireTemplateCreatorId,
    questionnaireTemplateId: formData.questionnaireTemplateId,
  });
  const [aiAgentsReadyToRun, setAiAgentsReadyToRun] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (aiAgents) {
      timer = setTimeout(() => {
        setAiAgentsReadyToRun(true);
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [aiAgents]);

  useEffect(() => {
    const values: Record<string, unknown> = {};
    (questionnaireResponses ?? []).forEach(response => {
      if (response.fieldTemplate.type !== 'list' && response.fieldTemplate.type !== 'table') {
        values[response.fieldTemplate.key] = response.value;
      } else {
        const listResponse = response.listResponses?.reduce(
          (acc: Record<string, unknown>, item) => ({
            ...acc,
            [item.fieldTemplate.key]: item.value,
          }),
          {
            _associatedWithId: response.associatedWithId,
            _existsInDb: true,
            _id: response.id,
          },
        );
        values[response.fieldTemplate.key] = values[response.fieldTemplate.key]
          ? [...(values[response.fieldTemplate.key] as Array<unknown>), listResponse]
          : [listResponse];
      }
    });
    setFormValues(values);
    formMethods.reset(values);
  }, [formMethods, questionnaireResponses]);

  useEffect(() => {
    const sources: Record<string, QuestionnaireResponse['source']> = {};
    const aiRequests: Record<string, string | null> = {};
    (questionnaireResponses ?? []).forEach(response => {
      if (response.fieldTemplate.type !== 'list' && response.fieldTemplate.type !== 'table') {
        sources[response.fieldTemplate.key] = response.source;
        aiRequests[response.fieldTemplate.key] = response.aiRequestId ?? null;
      } else {
        const listSources = response.listResponses?.reduce(
          (acc: Record<string, QuestionnaireResponse['source']>, item) => ({
            ...acc,
            [`${response.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.source,
          }),
          {},
        );
        const listAiRquests = response.listResponses?.reduce(
          (acc: Record<string, string | null>, item) => {
            if(item.source!=='ai') {
              return acc;
            }
            return (
              {
                ...acc,
                [`${response.fieldTemplate.key}[${item.index}].${item.fieldTemplate.key}`]: item.aiRequestId ?? null,
              }
            );
          },
          {},
        );
        Object.assign(sources, listSources);
        Object.assign(aiRequests, listAiRquests);
      }
    });
    responseSources.current = sources;
    aiRequestIdRef.current = aiRequests;
  }, [questionnaireResponses]);

  useEffect(() => {
    if (formData.creatorId) {
      getCreatorCreditorsFn({
        creatorId: formData.creatorId,
        page: 1,
        perPage: 100_000,
      });
    }
  }, [formData.creatorId, getCreatorCreditorsFn]);

  const refetchCreditors = useCallback((): void => {
    if (formData.creatorId) {
      getCreatorCreditorsFn({
        creatorId: formData.creatorId,
        page: 1,
        perPage: 100_000,
      });
    }
  }, [formData.creatorId, getCreatorCreditorsFn]);

  useEffect(() => {
    formData.sections.forEach(section => {
      section.fields.forEach(field => {
        const validationRules: Record<string, unknown> = {};

        if (field.isRequired) {
          validationRules.required = 'This field is required';
        }

        if (field.type === 'name') {
          validationRules.validate = validateName;
        } else if (field.type === 'currency') {
          validationRules.validate = validateCurrency;
        } else if (field.type === 'address-us') {
          validationRules.validate = (value: unknown) => validateUSAddress(value, field.isRequired);
        } else if (field.type === 'address-international') {
          validationRules.validate = validateIntlAddress;
        }

        formMethods.register(field.key, validationRules);
      });
    });
  }, [formMethods, formData.sections]);

  const [currentSection, setCurrentSection] = useState(formData.sections.length > 0 ? formData.sections[0].id : '');
  const [sectionErrors, setSectionErrors] = useState<{ [key: string]: boolean }>({});
  const [referenceData, setReferenceData] = useState<QuestionnaireApiModels.DataReference[]>([]);

  const [sectionVisibility, setSectionVisibilityState] = useState<Record<string, boolean>>({});
  const setSectionVisibility = useCallback((sectionId: string, isVisible: boolean): void => {
    setSectionVisibilityState(prevState => prevState[sectionId] !== isVisible
      ? {
        ...prevState,
        [sectionId]: isVisible,
      }
      : prevState,
    );
  }, []);

  const getFieldSource = useCallback((fieldKey: string): QuestionnaireResponse['source'] => responseSources.current[fieldKey], []);

  const getAiRequestId = useCallback((fieldKey: string): string | null => aiRequestIdRef.current[fieldKey] ?? null, []);

  const setFieldSource = useCallback((fieldKey: string, source: QuestionnaireResponse['source']): void => {
    responseSources.current = {
      ...responseSources.current,
      [fieldKey]: source,
    };
  }, []);

  const setAiRequestId = useCallback((fieldKey: string, aiRequestIdLocal:string): void => {
    aiRequestIdRef.current = {
      ...aiRequestIdRef.current,
      [fieldKey]: aiRequestIdLocal,
    };
  }, []);

  const getSectionProgress = useCallback(
    (sectionId: string, fieldsVisibility: Record<string, boolean>): number => {
      const section = formData.sections.find(_section => _section.id === sectionId);
      if (!section) return 0;

      let totalRequiredFields = section.fields
        .filter(field => !field.listFieldTemplateId && fieldsVisibility[field.key])
        .filter(field => field.isRequired && field.type !== 'explanation').length;

      let completedRequiredFields = 0;

      section.fields
        .filter(field => !field.listFieldTemplateId && fieldsVisibility[field.key])
        .forEach(field => {
          if (field.isRequired) {
            if (field.type === 'list') {
              const fieldValue = (formMethods.getValues()[field.key] || []) as Array<Record<string, unknown>>;
              if (fieldValue.length > 0) {
                completedRequiredFields += 1;
              }
              fieldValue.forEach(listItemValue => {
                if (!listItemValue._associatedWithId) {
                  field.listItemTemplates?.forEach(subField => {
                    let isVisible = true;

                    if (subField.conditionExpression && subField.conditionExpression.expression) {
                      const variablesWithValues = subField.conditionExpression.variables?.map(variable => ({
                        ...variable,
                        value: listItemValue[variable.referenceId],
                      })) || [];

                      const result = evaluateExpression({
                        expression: subField.conditionExpression.expression,
                        id: 'getSectionProgress1',
                        variables: variablesWithValues.reduce(
                          (acc, val) => ({
                            ...acc,
                            [val.variableName]: val.value,
                          }),
                          {},
                        ),
                      });

                      isVisible = Boolean(result);
                    }

                    if (isVisible && subField.isRequired) {
                      totalRequiredFields += 1;
                      const subFieldValue = listItemValue[subField.key];

                      const isFieldValid = checkFieldValidity(subField, subFieldValue);

                      if (isFieldValid) {
                        completedRequiredFields += 1;
                      }
                    }
                  });
                }
              });
            } else if (field.type === 'table') {
              // table is always "complete"
              completedRequiredFields += 1;

              const fieldValue = (formMethods.getValues()[field.key] || []) as Array<Record<string, unknown>>;

              field.tableColumns.forEach((column, idx) => {
                const { conditionExpression } = column;
                let isVisible;
                const conditionVariables = formMethods.watch(
                  _.uniq(
                    field.tableColumns
                      ?.map(c => c.conditionExpression?.variables?.map(v => v.referenceId) || [])
                      .reduce((acc, val) => [...acc, ...val]),
                  ),
                );
                if (conditionExpression?.expression) {
                  const variablesWithValues = conditionExpression.variables?.map((variable, varIdx) => ({
                    ...variable,
                    value: getFieldValue({
                      field: fieldsByKeyRef.current[variable.referenceId],
                      value: conditionVariables[varIdx],
                    }),
                  })) || [];
                  const result = evaluateExpression({
                    expression: conditionExpression.expression,
                    id: 'getSectionProgress2',
                    variables: variablesWithValues.reduce(
                      (acc, val) => ({
                        ...acc,
                        [val.variableName]: val.value,
                      }),
                      {},
                    ),
                  });
                  isVisible = Boolean(result);
                } else {
                  isVisible = true;
                }
                if (isVisible) {
                  field?.listItemTemplates?.forEach(subField => {
                    if (fieldsVisibility[subField.key] && subField.isRequired) {
                      totalRequiredFields += 1;
                      const subFieldValue = fieldValue[idx]?.[subField.key];

                      const isFieldValid = checkFieldValidity(subField, subFieldValue);

                      if (isFieldValid) {
                        completedRequiredFields += 1;
                      }
                    }
                  });
                }
              });
            } else if (field.type === 'median-income' || field.type === 'monthly-income') {
              // median and monthly income are always "complete"
              completedRequiredFields += 1;
            } else {
              const fieldKey = field.key;
              const fieldValue = formMethods.getValues()[fieldKey];

              const isFieldValid = checkFieldValidity(field, fieldValue);

              if (isFieldValid) {
                completedRequiredFields += 1;
              }
            }
          }
        });

      return totalRequiredFields === 0 ? 100 : (completedRequiredFields / totalRequiredFields) * 100;
    },
    [formData, formMethods],
  );

  const getSubsectionProgress = useCallback(
    (
      sectionId: string,
      subsection: string,
      fieldsVisibility: Record<string, boolean>,
    ): {
      completedFields: number;
      subsectionProgressPercentage: number | null;
      totalFields: number;
    } | null => {
      const section = formData.sections.find(_section => _section.id === sectionId);
      if (!section) return null;

      const fieldsInSubsectionKeys = section.fields
        .filter(field => field.subsection === subsection && fieldsVisibility[field.key] && field.type !== 'explanation' && field.isRequired)
        .map(field => field.key);

      const subsectionFieldsVisibility = Object.fromEntries(Object.entries(fieldsVisibility).filter(([key]) => fieldsInSubsectionKeys.includes(key)));

      // reuse getSectionProgress by passing the fields in the subsection
      const subsectionProgressPercentage = fieldsInSubsectionKeys.length ? getSectionProgress(sectionId, subsectionFieldsVisibility) : null;

      // calculate completedFields and totalFields based on the percentage
      const totalFields = fieldsInSubsectionKeys.length;
      const completedFields = subsectionProgressPercentage ? Math.round((subsectionProgressPercentage / 100) * totalFields) : 0;

      return {
        completedFields,
        subsectionProgressPercentage,
        totalFields,
      };
    },
    [formData, getSectionProgress],
  );

  const setValue = useCallback<FormContextType['setValue']>(
    ({ key, value, source, aiRequestId }): void => {
      // @todo - it would be nice if we checked the source here and didn't allow setting if "manual" unless new source is also "manual"
      formMethods.setValue(key, value, { shouldDirty: true });
      if (source) {
        setFieldSource(key, source);
      }
      if(aiRequestId) {
        setAiRequestId( key, aiRequestId);
      }
    },
    [formMethods, setFieldSource, setAiRequestId],
  );

  const fieldsById: Record<string, Questionnaire.Field> = useMemo(
    () =>
      Object.values(fieldsByKey).reduce(
        (acc, val) => ({
          ...acc,
          [val.id]: val,
        }),
        {},
      ),
    [fieldsByKey],
  );

  const values = useMemo(
    () => {
      trackEvents.track('FormContext - valuesMemoized');
      return {
        aiAgents,
        aiAgentsReadyToRun,
        aiAutofillActions,
        checkFieldValidity,
        creditors: creatorCreditors?.items ?? [],
        currentSection,
        defaultValues: formValues,
        fieldsById,
        fieldsByKey,
        formMethods,
        getAiRequestId,
        getFieldSource,
        getSectionProgress,
        getSubsectionProgress,
        onSubmit,
        questionnaireId: formData.id,
        referenceData,
        refetchCreditors,
        responseSources: responseSources.current,
        sectionErrors,
        sectionVisibility,
        setAiRequestId,
        setCurrentSection,
        setFieldSource,
        setReferenceData,
        setSectionErrors,
        setSectionVisibility,
        setValue,
        userWorkflowId,
      };
    },
    [
      aiAgents,
      aiAgentsReadyToRun,
      aiAutofillActions,
      creatorCreditors,
      currentSection,
      fieldsById,
      fieldsByKey,
      formData,
      formMethods,
      formValues,
      getAiRequestId,
      getFieldSource,
      getSectionProgress,
      getSubsectionProgress,
      onSubmit,
      referenceData,
      refetchCreditors,
      responseSources,
      sectionErrors,
      sectionVisibility,
      setAiRequestId,
      setCurrentSection,
      setFieldSource,
      setReferenceData,
      setSectionErrors,
      setSectionVisibility,
      setValue,
      userWorkflowId,
    ],
  );

  return <FormContext.Provider value={values}>{children}</FormContext.Provider>;
};
